import { t as tr } from '../i18n.js'
import { useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Navigate, useParams } from 'react-router'
import { api, ApiError, browserTimezone, type User } from '../api.js'
import { useAuth } from '../auth.js'

export function AcceptInvitePage() {
  const { token = '' } = useParams()
  const { user, refresh } = useAuth()
  const preview = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<{ email: string; role: string; googleEnabled: boolean; isReset: boolean }>(`/auth/invites/${token}/preview`),
    retry: false,
  })
  const [displayName, setDisplayName] = useState('')
  const [timezone, setTimezone] = useState(browserTimezone())
  const [password, setPassword] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (user) return <Navigate to="/" replace />

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (password !== confirmPw) { setError('The two passwords do not match.'); return }
    setBusy(true); setError(null)
    try {
      await api<{ user: User }>(`/auth/invites/${token}/accept`, { method: 'POST', json: { displayName, timezone, password } })
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not accept invite')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="shell narrow">
      <header>
        <h1>ADKOM Product Manager</h1>
        <p className="muted">{preview.data?.isReset ? tr("Reset your password") : tr("Set up your account")}</p>
      </header>
      <section className="card stack">
        {preview.isPending && <p className="muted">{tr("Checking invite…")}</p>}
        {preview.isError && <p className="bad">{tr("This invite is invalid or has expired. Ask an administrator for a new one.")}</p>}
        {preview.data && (
          <>
            {preview.data.isReset
              ? <p>{tr("Set a new password for")} <strong>{preview.data.email}</strong>. Your other sessions will be signed out.</p>
              : <p>{tr("You have been invited as")} <strong>{preview.data.email}</strong> ({preview.data.role}).</p>}
            {preview.data.googleEnabled && (
              <>
                <a className="button primary block" href={`/api/auth/google?tz=${encodeURIComponent(browserTimezone())}`}>{tr("Continue with Google")}</a>
                <div className="divider">{tr("or set a password")}</div>
              </>
            )}
            <form onSubmit={submit} className="stack">
              <label>{tr("Your name")}
                <input required maxLength={200} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </label>
              <label>{tr("Timezone")}
                <input required value={timezone} onChange={(e) => setTimezone(e.target.value)} />
                <span className="hint">{tr("Your own zone, for display. Projects keep their own timezone-of-truth.")}</span>
              </label>
              <label>{preview.data.isReset ? tr("New password") : tr("Password")}
                <input type="password" autoComplete="new-password" required minLength={12} maxLength={256} value={password} onChange={(e) => setPassword(e.target.value)} />
                <span className="hint">{tr("At least 12 characters. A short sentence works well.")}</span>
              </label>
              <label>{tr("Confirm password")}
                <input type="password" autoComplete="new-password" required minLength={12} maxLength={256} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} aria-invalid={confirmPw !== '' && confirmPw !== password} />
                {confirmPw !== '' && confirmPw !== password && <span className="hint bad">{tr("Does not match.")}</span>}
              </label>
              {error && <p className="bad" role="alert">{error}</p>}
              <button className="button" type="submit" disabled={busy}>{busy ? 'Saving…' : preview.data.isReset ? 'Set new password' : 'Create account'}</button>
            </form>
          </>
        )}
      </section>
    </main>
  )
}
